import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem,
} from '@mui/material';
import { Home, Add, Edit, Delete, MenuBook, Inventory2, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;
const CATEGORIES = ['Textbook', 'Reference', 'PastPaper', 'Archive', 'Other'];
const CONDITIONS = ['New', 'Good', 'Fair', 'Poor', 'Lost'];
const emptyBook = { title: '', author: '', isbn: '', category: 'Textbook', learningArea: '', stage: '', publisher: '', edition: '', isArchived: false, notes: '' };

export default function LibraryCatalog() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [storageAreas, setStorageAreas] = useState([]);
  const [stages, setStages] = useState([]);
  const [learningAreas, setLearningAreas] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const [bookDlg, setBookDlg] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [bookForm, setBookForm] = useState(emptyBook);
  const [delBook, setDelBook] = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: bookDlg, value: bookForm, onClose: () => setBookDlg(false),
    message: `You have unsaved book details. Close without saving?`,
  });

  const [copiesFor, setCopiesFor] = useState(null); // book obj
  const [copies, setCopies] = useState([]);
  const [copyForm, setCopyForm] = useState({ barcode: '', storageArea: '', condition: 'Good' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/api/library/books`, { headers }),
        axios.get(`${API}/api/library/storage-areas`, { headers }),
      ]);
      setBooks(a.data); setStorageAreas(b.data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Reference lists for the dropdowns — fetched separately so a missing academics
  // permission can't break the catalog load.
  useEffect(() => {
    axios.get(`${API}/api/stages`, { headers }).then(r => setStages(r.data)).catch(() => {});
    axios.get(`${API}/api/academics/learning-areas`, { headers }).then(r => setLearningAreas(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const openNewBook = () => { setEditingBook(null); setBookForm(emptyBook); setBookDlg(true); };
  const openEditBook = (b) => { setEditingBook(b.bookNo); setBookForm({ ...emptyBook, ...b }); setBookDlg(true); };
  const saveBook = async () => {
    if (!bookForm.title.trim()) return setError('Title is required');
    try {
      if (editingBook) await axios.put(`${API}/api/library/books/${editingBook}`, bookForm, { headers });
      else await axios.post(`${API}/api/library/books`, bookForm, { headers });
      setBookDlg(false); setError(''); setSuccess(`Book ${editingBook ? 'updated' : 'added'}`); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
  };
  const removeBook = async () => {
    try { await axios.delete(`${API}/api/library/books/${delBook}`, { headers }); setDelBook(null); setSuccess('Book deleted'); load(); }
    catch (err) { setDelBook(null); setError(err.response?.data?.error || 'Failed to delete'); }
  };

  const openCopies = async (b) => {
    setCopiesFor(b); setCopyForm({ barcode: '', storageArea: '', condition: 'Good' });
    try { const { data } = await axios.get(`${API}/api/library/copies?bookNo=${b.bookNo}`, { headers }); setCopies(data); }
    catch (err) { setError(err.response?.data?.error || 'Failed to load copies'); }
  };
  const addCopy = async () => {
    if (!copyForm.barcode.trim()) return setError('Barcode is required');
    try {
      await axios.post(`${API}/api/library/copies`, { ...copyForm, bookNo: copiesFor.bookNo }, { headers });
      const { data } = await axios.get(`${API}/api/library/copies?bookNo=${copiesFor.bookNo}`, { headers });
      setCopies(data); setCopyForm({ barcode: '', storageArea: '', condition: 'Good' }); setError(''); setSuccess('Copy added');
    } catch (err) { setError(err.response?.data?.error || 'Failed to add copy'); }
  };
  const delCopy = async (barcode) => {
    try {
      await axios.delete(`${API}/api/library/copies/${barcode}`, { headers });
      const { data } = await axios.get(`${API}/api/library/copies?bookNo=${copiesFor.bookNo}`, { headers });
      setCopies(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete copy'); }
  };

  if (loading) return (<Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center"><MenuBook sx={{ mr: 1, color: '#5e35b1' }} /><Typography variant="h5" sx={{ color: '#5e35b1' }}>Books &amp; Copies</Typography></Box>
            <Box>
              <IconButton onClick={() => navigate('/library')} title="Library Dashboard" sx={{ mr: 1 }}><Home /></IconButton>
              <IconButton onClick={openNewBook} title="Add Book"><Add /></IconButton>
            </Box>
          </Box>
          <Toast error={error} success={success} onClose={(k) => k === 'error' ? setError('') : setSuccess('')} />
          {books.length === 0 ? <Typography color="text.secondary">No books yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Book No</strong></TableCell><TableCell><strong>Title</strong></TableCell><TableCell><strong>Author</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell><TableCell><strong>Stage</strong></TableCell>
                <TableCell align="center"><strong>Copies (avail/total)</strong></TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {books.map(b => (
                  <TableRow key={b.bookNo} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{b.bookNo}</TableCell>
                    <TableCell>{b.title}{b.isArchived && <Chip label="Archived" size="small" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell>{b.author}</TableCell>
                    <TableCell><Chip label={b.category} size="small" variant="outlined" /></TableCell>
                    <TableCell>{b.stage}</TableCell>
                    <TableCell align="center">{b.availableCopies}/{b.totalCopies}</TableCell>
                    <TableCell>
                      <IconButton size="small" title="Manage copies" onClick={() => openCopies(b)}><Inventory2 fontSize="small" /></IconButton>
                      <IconButton color="primary" size="small" onClick={() => openEditBook(b)}><Edit fontSize="small" /></IconButton>
                      <IconButton color="warning" size="small" onClick={() => setDelBook(b.bookNo)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Book dialog */}
      {discardDialog}
      <Dialog open={bookDlg} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingBook ? `Edit Book: ${editingBook}` : 'New Book'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Title" value={bookForm.title} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, title: e.target.value })} />
            <TextField label="Author" value={bookForm.author} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, author: e.target.value })} />
            <Box display="flex" gap={2}>
              <TextField label="ISBN" value={bookForm.isbn} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, isbn: e.target.value })} />
              <TextField select label="Category" value={bookForm.category} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, category: e.target.value })}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Box>
            <Box display="flex" gap={2}>
              <TextField select label="Learning Area" value={bookForm.learningArea} size="small" fullWidth
                onChange={e => setBookForm({ ...bookForm, learningArea: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {learningAreas.map(a => <MenuItem key={a.areaCode} value={a.areaCode}>{a.areaCode} — {a.areaName}</MenuItem>)}
              </TextField>
              <TextField select label="Stage" value={bookForm.stage} size="small" fullWidth
                onChange={e => setBookForm({ ...bookForm, stage: e.target.value })}>
                <MenuItem value=""><em>— None —</em></MenuItem>
                {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Publisher" value={bookForm.publisher} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, publisher: e.target.value })} />
              <TextField label="Edition" value={bookForm.edition} size="small" fullWidth onChange={e => setBookForm({ ...bookForm, edition: e.target.value })} />
            </Box>
            <TextField label="Notes" value={bookForm.notes} size="small" fullWidth multiline minRows={2} onChange={e => setBookForm({ ...bookForm, notes: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={attemptClose}>Cancel</Button><Button variant="contained" onClick={saveBook}>Save</Button></DialogActions>
      </Dialog>

      {/* Delete book */}
      <Dialog open={Boolean(delBook)} onClose={() => setDelBook(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}><WarningAmber color="warning" /> Delete Book</DialogTitle>
        <DialogContent><Typography>Delete <strong>{delBook}</strong>? (copies must be removed first)</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDelBook(null)}>Cancel</Button><Button variant="contained" color="warning" onClick={removeBook}>Delete</Button></DialogActions>
      </Dialog>

      {/* Copies dialog */}
      <Dialog open={Boolean(copiesFor)} onClose={() => setCopiesFor(null)} fullWidth maxWidth="md">
        <DialogTitle>Copies — {copiesFor?.title}</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={1} alignItems="center" my={1} flexWrap="wrap">
            <TextField label="Barcode" size="small" value={copyForm.barcode} onChange={e => setCopyForm({ ...copyForm, barcode: e.target.value })} />
            <TextField select label="Storage Area" size="small" sx={{ minWidth: 160 }} value={copyForm.storageArea} onChange={e => setCopyForm({ ...copyForm, storageArea: e.target.value })}>
              <MenuItem value="">(none)</MenuItem>
              {storageAreas.map(s => <MenuItem key={s.storageCode} value={s.storageCode}>{s.name}</MenuItem>)}
            </TextField>
            <TextField select label="Condition" size="small" sx={{ minWidth: 120 }} value={copyForm.condition} onChange={e => setCopyForm({ ...copyForm, condition: e.target.value })}>
              {CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Button variant="outlined" startIcon={<Add />} onClick={addCopy}>Add copy</Button>
          </Box>
          {copies.length === 0 ? <Typography color="text.secondary">No copies yet.</Typography> : (
            <Table size="small">
              <TableHead><TableRow><TableCell><strong>Barcode</strong></TableCell><TableCell><strong>Storage</strong></TableCell><TableCell><strong>Condition</strong></TableCell><TableCell><strong>Status</strong></TableCell><TableCell></TableCell></TableRow></TableHead>
              <TableBody>
                {copies.map(c => (
                  <TableRow key={c.barcode}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{c.barcode}</TableCell>
                    <TableCell>{c.storageArea}</TableCell>
                    <TableCell>{c.condition}</TableCell>
                    <TableCell><Chip label={c.status} size="small" color={c.status === 'Available' ? 'success' : c.status === 'OnLoan' ? 'info' : 'default'} variant="outlined" /></TableCell>
                    <TableCell><IconButton size="small" color="warning" onClick={() => delCopy(c.barcode)}><Delete fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setCopiesFor(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
